import { useState } from 'react';
import { checkOutboundNumber, formatPhoneNumber } from '@avslack/shared';
import { useChannels } from '../data/channels';
import { findChannelHuddle, useActiveHuddles } from '../data/huddles';
import { useCallableLines, useMyPhoneLines, usePhoneContacts } from '../data/phone';
import { belongsToTeam, useActiveTeam } from '../data/teams';
import { useUsers } from '../data/users';
import { openDm } from '../lib/api';
import { openHuddleWindow } from '../lib/huddle-window';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { LinePicker } from './LinePicker';
import { useDialFromChannel } from './PhoneLegs';
import { TtDirectory } from './TtDirectory';
import { useCustomerSystem } from '../lib/session';

/**
 * HIVAS INDITASA a vevoszolgalati panelbol (docs/20-vevoszolgalat.html).
 *
 * Egy kereso, harom talalati csoporttal - es a kolléga NEM kell, hogy tudja, melyik
 * hivasfajta melyik:
 *
 *  - KOLLEGAK (AVSlack): a "Olelés" gomb egy kozvetlen uzenet csatornan indit hivast;
 *  - CSATORNAK: a csatorna olelese (vagy csatlakozas, ha mar folyik) - csak az aktiv ceg
 *    csatornai, a telefonos vonalak sajat csatornai nelkul (mint az oldalsavban);
 *  - CIMJEGYZEK: a mar ismert / elnevezett szamok;
 *  - TT UGYFELEK: a TT-ben kereshetok, a szamuk a kattintasra toltodik be (lasd
 *    `TtDirectory`) - ugyanaz a komponens, mint a telefonos csatorna cimjegyzeke;
 *  - beirt SZAM: kozvetlenul hivhato.
 *
 * A lebego ablakban rajzolodik (React-portal), ezert csak olyat hasznal, ami nem a fo
 * dokumentumra kot: nincs Radix-felugro, a valaszto natív <select>.
 *
 * A telefonos hivas a felhasznalo egyik hivhato VONALANAK csatornajan indul (lasd
 * `useDialFromChannel`); kifele hivni csak az admin altal adott joggal lehet.
 */

const MAX_PEOPLE = 5;
const MAX_CHANNELS = 5;
const MAX_CONTACTS = 4;

export function ServiceDial({ meId }: { meId: string }) {
  const { users, nameOf } = useUsers();
  const { contacts } = usePhoneContacts();
  const canCallOut = users.find((user) => user.id === meId)?.canCallOut ?? false;
  // A TT ugyfelei csak TT-s kollegának (lasd lib/session.ts).
  const customerSystem = useCustomerSystem();

  // A hivas a vonal telefonos csatornajan indul; csatorna nelkuli vonalrol nem tudunk innen hivni.
  const { data: allLines } = useCallableLines(canCallOut);
  const lines = (allLines ?? []).filter((line) => line.channelId);
  const [lineId, setLineId] = useState<string | null>(null);
  const line = lines.find((item) => item.id === lineId) ?? lines[0] ?? null;
  const callBack = useDialFromChannel(line?.channelId ?? '', meId);

  const { data: channels } = useChannels();
  const { data: huddles } = useActiveHuddles();
  const { data: myPhoneLines } = useMyPhoneLines();
  const { activeTeamId } = useActiveTeam();

  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const needle = query.trim().toLocaleLowerCase('hu');
  const digits = needle.replace(/\D/g, '');
  const looksLikeNumber = digits.length >= 3 && /^[\d\s+()/-]+$/.test(query.trim());

  const people = needle
    ? users
        .filter(
          (user) =>
            !user.isBot &&
            user.id !== meId &&
            (nameOf(user).toLocaleLowerCase('hu').includes(needle) ||
              user.displayName.toLocaleLowerCase('hu').includes(needle)),
        )
        .slice(0, MAX_PEOPLE)
    : [];

  // A vonalak SAJAT csatornai nem beszelgetesek, hanem a hivasok helye (lasd Sidebar).
  const ownLineChannels = new Set(
    (myPhoneLines ?? []).filter((item) => item.place === 'own').map((item) => item.channelId),
  );
  const channelMatches = needle
    ? (channels ?? [])
        .filter(
          (channel) =>
            (channel.kind === 'public' || channel.kind === 'private') &&
            belongsToTeam(channel, activeTeamId) &&
            !ownLineChannels.has(channel.id) &&
            (channel.name ?? '').toLocaleLowerCase('hu').includes(needle),
        )
        .slice(0, MAX_CHANNELS)
    : [];

  const known = needle
    ? contacts
        .filter(
          (contact) =>
            (contact.name ?? '').toLocaleLowerCase('hu').includes(needle) ||
            (digits.length >= 3 && contact.number.replace(/\D/g, '').includes(digits)),
        )
        .slice(0, MAX_CONTACTS)
    : [];

  const numberCheck = looksLikeNumber ? checkOutboundNumber(query) : null;
  const phoneBusy = callBack.busy || !line;

  const callUser = async (userId: string) => {
    setError(null);
    setBusyUserId(userId);
    try {
      const { channel } = await openDm([userId]);
      // Vevoszolgalati modban ez a fo ablakban lep be (nincs felugro ablak).
      const opened = await openHuddleWindow(channel.id);
      if (!opened) setError('A hívás nem indult el – nézd meg a fő ablakot.');
      else setQuery('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'A hívás nem indult el.');
    } finally {
      setBusyUserId(null);
    }
  };

  const callChannel = (channelId: string) => {
    setError(null);
    void openHuddleWindow(channelId).then((opened) => {
      if (!opened) setError('A hívás nem indult el – nézd meg a fő ablakot.');
      else setQuery('');
    });
  };

  const dial = (number: string) => {
    if (!line) return;
    setError(null);
    callBack.dial(number, line.id);
    setQuery('');
  };

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <label className="relative block">
        <span className="sr-only">Hívás indítása</span>
        <span aria-hidden className="pointer-events-none absolute left-2.5 top-2 text-ink-faint">
          <Icon name="keres" size="sm" />
        </span>
        <input
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setError(null);
          }}
          placeholder="Kolléga, ügyfél vagy telefonszám…"
          className="w-full rounded-control border border-border bg-surface py-1.5 pl-8 pr-2.5 text-sm"
        />
      </label>

      {lines.length > 1 && (
        <LinePicker lines={lines} value={line?.id ?? null} onChange={setLineId} />
      )}

      {!needle && (
        <p className="text-2xs text-ink-faint">
          Kezdj gépelni: kolléga vagy csatorna neve (ölelés), ügyfél vagy telefonszám
          (telefonhívás).
        </p>
      )}

      {needle && (
        <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-0.5">
          {people.length > 0 && (
            <Section title="Kollégák">
              {people.map((user) => (
                <li key={user.id} className="flex items-center gap-2 py-1">
                  <Avatar
                    userId={user.id}
                    name={user.displayName}
                    avatarUrl={user.avatarUrl}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{nameOf(user)}</span>
                  <button
                    type="button"
                    disabled={busyUserId !== null || callBack.busy}
                    onClick={() => void callUser(user.id)}
                    title="Ölelés indítása"
                    className="flex shrink-0 items-center gap-1 rounded-control bg-accent px-2 py-1 text-xs font-semibold text-accent-ink disabled:opacity-50"
                  >
                    <Icon name="huddle" size="sm" /> Ölelés
                  </button>
                </li>
              ))}
            </Section>
          )}

          {channelMatches.length > 0 && (
            <Section title="Csatornák">
              {channelMatches.map((channel) => {
                const live = findChannelHuddle(huddles, channel.id);
                const inside = live?.participants.filter((p) => p.state === 'joined').length ?? 0;
                return (
                  <li key={channel.id} className="flex items-center gap-2 py-1">
                    <span aria-hidden className="w-5 shrink-0 text-center text-ink-faint">
                      {channel.kind === 'private' ? <Icon name="lakat" size="sm" /> : '#'}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">{channel.name}</span>
                    <button
                      type="button"
                      disabled={callBack.busy}
                      onClick={() => callChannel(channel.id)}
                      title={live ? 'Csatlakozás az öleléshez' : 'Ölelés indítása a csatornában'}
                      className="flex shrink-0 items-center gap-1 rounded-control bg-accent px-2 py-1 text-xs font-semibold text-accent-ink disabled:opacity-50"
                    >
                      <Icon name="huddle" size="sm" /> {live ? `Csatlakozás · ${inside}` : 'Ölelés'}
                    </button>
                  </li>
                );
              })}
            </Section>
          )}

          {numberCheck && (
            <Section title="Telefonszám">
              <li className="flex items-center gap-2 py-1">
                <span className="min-w-0 flex-1 truncate text-sm tabular-nums">
                  {numberCheck.ok ? formatPhoneNumber(numberCheck.number) : query.trim()}
                </span>
                {numberCheck.ok ? (
                  <DialButton
                    disabled={!canCallOut || phoneBusy}
                    onClick={() => dial(numberCheck.number)}
                  />
                ) : (
                  <span className="shrink-0 text-2xs text-ink-faint">{numberCheck.reason}</span>
                )}
              </li>
            </Section>
          )}

          {known.length > 0 && (
            <Section title="Címjegyzék">
              {known.map((contact) => (
                <li key={contact.number} className="flex items-center gap-2 py-1">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{contact.name || contact.number}</span>
                    <span className="block truncate text-2xs tabular-nums text-ink-faint">
                      {[contact.companyName, formatPhoneNumber(contact.number)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <DialButton
                    disabled={!canCallOut || phoneBusy}
                    onClick={() => dial(contact.number)}
                  />
                </li>
              ))}
            </Section>
          )}

          {/* A TT ugyfelei: 3 betutol keres, a szamok a kattintasra toltodnek be. */}
          {customerSystem === 'tt' && (
            <TtDirectory query={query} canCallOut={canCallOut} busy={phoneBusy} onDial={dial} />
          )}

          {people.length === 0 &&
            channelMatches.length === 0 &&
            !numberCheck &&
            known.length === 0 &&
            needle.length < 3 && <p className="text-xs text-ink-muted">Nincs találat.</p>}
        </div>
      )}

      {needle && !canCallOut && (
        <p className="text-2xs text-ink-faint">
          Telefonszámot hívni az admin által adott joggal lehet; a kollégákat hívhatod.
        </p>
      )}
      {needle && canCallOut && !line && (
        <p className="text-2xs text-ink-faint">
          Nincs hívható telefonvonalad, amihez telefonos csatorna tartozna.
        </p>
      )}
      {callBack.busy && (
        <p className="text-2xs text-ink-muted">Egy másik hívásban vagy – előbb lépj ki belőle.</p>
      )}
      {(error || callBack.error) && (
        <p role="alert" className="text-xs text-danger">
          {error ?? callBack.error}
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-0.5 text-2xs font-medium uppercase tracking-wide text-ink-faint">{title}</p>
      <ul className="divide-y divide-border-soft">{children}</ul>
    </div>
  );
}

function DialButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title="Telefonhívás indítása"
      className="flex shrink-0 items-center gap-1 rounded-control bg-accent px-2 py-1 text-xs font-semibold text-accent-ink disabled:opacity-50"
    >
      <Icon name="telefon" size="sm" /> Hívás
    </button>
  );
}
